import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { _getTimeDetails } from './utils';
import Clock from './Clock/Clock';
import classNames from 'classnames';
import { startOfDay } from 'date-fns';
import { getInputSizeStyles, getInputStateClass } from '../utils';
import { UXPContext } from "@core/context/Context";
import { Dropdown } from '@widget/popups/Dropdown';
import DropdownIndicator from "@widget/other/DropdownIndicator";
import { FontAwesomeIconWrapper } from "@icon/fontawesome/FontAwesomeIconWrapper";
import Input from '../Input';
import ButtonComponent from "@widget/buttons/ButtonComponent";
/**
 *
 * @export
 *
 * This component is used to select a time.
 *
 * @example
 * ```
 *  <TimePicker
 *      title="Time"
 *      time={date}
 *      onChange={(date) => setDate(date)}
 *  />
 * ```
 *
 * @category Inputs/Date and Time
 */
const TimePicker = (props) => {
    const context = React.useContext(UXPContext);
    //props
    let { time, title = context.$L('uxp-core.text.select-a-time'), onChange, disableInput, hideLabels, dropdownClassname, minWidth, maxWidth, width, fullWidth, disabled, readOnly, isValid: isValidProp, dropdownMaxWidth, onClear } = props;
    // state
    let [showPicker, setShowPicker] = React.useState(false);
    let [timeObj, setTimeObj] = React.useState({ hours: null, mins: null, secs: null, evening: false, timeString: null });
    let [showInput, setShowInput] = React.useState(false);
    let [isFocused, setIsFocused] = React.useState(false);
    let [manualValue, setManualValue] = React.useState('');
    const triggerRef = React.useRef(null);
    React.useEffect(() => {
        if (time) {
            let _d = _getTimeDetails(time, true);
            setTimeObj(_d);
            setManualValue(_d.timeString);
        }
        else {
            setTimeObj({ hours: null, mins: null, secs: null, evening: false, timeString: null });
            setManualValue('');
        }
    }, [time]);
    const handleFocus = () => {
        if (!disabled && !readOnly) {
            setIsFocused(true);
        }
    };
    const handleBlur = () => {
        if (!disabled && !readOnly) {
            setIsFocused(false);
        }
    };
    const handleKeyDownOnTrigger = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowPicker(true);
        }
    };
    const handleTriggerClick = () => {
        if (!disabled && !readOnly) {
            setShowPicker(!showPicker);
        }
    };
    const closePicker = () => {
        setShowPicker(false);
        setShowInput(false);
        setTimeout(() => { var _a; return (_a = triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus(); }, 100);
    };
    // With saveDefaultOnDone, Done saves the time the clock shows when there is no value yet (today 00:00)
    const handleDone = () => {
        if (!time && props.saveDefaultOnDone)
            onChange(startOfDay(new Date()));
        closePicker();
    };
    const handleManualInputChange = (val) => {
        // Allow free-form editing, validation happens on blur
        setManualValue(val);
    };
    const handleManualInputBlur = () => {
        // Parse and validate on blur
        try {
            const _d = _getTimeDetails(manualValue, true);
            onChange(_d.date);
            setManualValue(_d.timeString); // Re-format to standard format
        }
        catch (e) {
            // Invalid input, reset to current valid value
            setManualValue(timeObj.timeString);
        }
    };
    const renderDropdownContent = () => (_jsxs("div", { className: "uxpcore_timepicker__dropdown-content", children: [_jsxs("div", { className: "uxpcore_timepicker__header", children: [_jsx("div", { className: "uxpcore_timepicker__title", children: showInput ? context.$L('uxp-core.text.enter-time') : title }), _jsx("div", { className: "uxpcore_timepicker__toggle-input", onClick: (e) => {
                            e.stopPropagation();
                            setShowInput(!showInput);
                        }, title: showInput ? context.$L('uxp-core.text.show-clock') : context.$L('uxp-core.text.type-time'), children: _jsx(FontAwesomeIconWrapper, { icon: ['far', showInput ? 'clock' : 'keyboard'] }) })] }), showInput && (_jsx("div", { className: "uxpcore_timepicker__input-container", children: _jsx(Input, { type: "text", value: manualValue, onChange: handleManualInputChange, onBlur: handleManualInputBlur, readOnly: disableInput, placeholder: "12:30 PM", fullWidth: true, hideClear: true }) })), !showInput && (_jsx(Clock, { time: time, onChange: onChange, disableInput: disableInput })), _jsx("div", { className: "uxpcore_timepicker__footer", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.done'), onClick: handleDone, size: "small" }) })] }));
    const containerStyles = getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined });
    const dropdownIndicator = _jsx(DropdownIndicator, { isOpen: showPicker });
    const stateClass = getInputStateClass({ disabled, readOnly, isValid: isValidProp });
    const clearButton = (!!time && !disabled && !readOnly) ? (_jsx("div", { className: "uxpcore_input__clear-btn", onClick: (e) => { e.stopPropagation(); onClear ? onClear() : onChange(null); }, children: _jsx(FontAwesomeIconWrapper, { icon: ['fas', 'times'] }) })) : null;
    return (_jsxs("div", { className: classNames("uxpcore_timepicker", { "uxpcore_input--full-width": fullWidth }), style: containerStyles, children: [_jsxs("div", { ref: triggerRef, className: classNames("uxpcore_input", "uxpcore_input--timepicker", stateClass, {
                    "uxpcore_input--has-prefix": !hideLabels,
                    "uxpcore_input--has-suffix": !!(clearButton || dropdownIndicator),
                    "uxpcore_input--has-content": !!time,
                    "uxpcore_input--focus": isFocused || showPicker,
                    "uxpcore_input--full-width": fullWidth
                }), onClick: handleTriggerClick, onKeyDown: handleKeyDownOnTrigger, onFocus: handleFocus, onBlur: handleBlur, tabIndex: disabled || readOnly ? -1 : 0, style: getInputSizeStyles({ minWidth, maxWidth, width, fullWidth, style: undefined }), children: [!hideLabels && (_jsx("div", { className: "uxpcore_input__prefix", children: _jsx(FontAwesomeIconWrapper, { icon: ['far', 'clock'] }) })), _jsx("div", { className: "uxpcore_input__value", children: timeObj.timeString || context.$L('uxp-core.text.select-a-time') }), _jsxs("div", { className: "uxpcore_input__suffix", children: [clearButton, dropdownIndicator] })] }), _jsx(Dropdown, { externalTriggerRef: triggerRef, content: renderDropdownContent(), position: "bottom-left", isOpen: showPicker, onToggle: closePicker, contentClassName: classNames("uxpcore_timepicker__dropdown", dropdownClassname), matchTriggerWidth: false, maxWidth: dropdownMaxWidth || '15rem', minWidth: "15rem" })] }));
};
export default TimePicker;
